import { memo, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { ZoomIn, X } from "lucide-react";
import ChoixRenderer from "../ChoixRenderer";
import { ratioSvg, svgDeDataUrl, styleAjuste } from "../zoomFit";

/**
 * Bouton loupe à poser dans une tuile de choix QCM visuel (image / droite / expression / geogebra).
 * - Taper la loupe → zoom PLEIN ÉCRAN device (modale portée sur document.body, donc hors du
 *   `transform` de la carte blanche du quiz) + fermer.
 * - Le clic est stoppé (stopPropagation + preventDefault) pour NE PAS sélectionner la réponse :
 *   la sélection reste sur le reste de la tuile (le <label>).
 *
 * Aucune logique de réponse touchée — purement de l'affichage/zoom.
 *
 * @param {string|Object} choix - Le choix brut (objet { type, config } pour un visuel)
 * @param {boolean} light - Thème clair (passation carte blanche) → rendu lisible au zoom
 */
const ChoixZoomButton = memo(function ChoixZoomButton({ choix, light = false }) {
    const [isZoomed, setIsZoomed] = useState(false);
    // Rapport largeur / hauteur d'une image raster, connu au chargement (zoomFit.js : l'agrandi tient dans l'écran, sans ascenseur).
    const [ratioNaturel, setRatioNaturel] = useState(null);

    // Bloquer le scroll du body quand le zoom est ouvert (comme les autres modales)
    useEffect(() => {
        if (!isZoomed) return;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, [isZoomed]);

    if (!choix || typeof choix !== 'object') return null;

    const isImage = choix.type === 'image';
    const imageSrc = isImage ? (choix.config?.imageUrl || choix.config?.imageData || '') : '';
    const svgDuSrc = isImage ? svgDeDataUrl(imageSrc) : null;
    const ratio = isImage ? (svgDuSrc ? ratioSvg(svgDuSrc) : ratioNaturel) : null;

    const open = (e) => {
        e.stopPropagation();
        e.preventDefault();
        setIsZoomed(true);
    };
    const close = () => setIsZoomed(false);

    return (
        <>
            {/* Loupe en coin de la tuile — ne sélectionne pas */}
            <button
                type="button"
                onClick={open}
                aria-label="Agrandir"
                title="Agrandir"
                className="absolute top-1.5 right-1.5 z-10 w-8 h-8 rounded-full bg-white/90 border border-slate-200 shadow-sm flex items-center justify-center text-slate-600 hover:bg-white hover:text-slate-900 transition-colors"
            >
                <ZoomIn className="w-4 h-4" />
            </button>

            {/* Modale plein écran (portée sur document.body) */}
            {isZoomed && createPortal(
                <div
                    className="fixed inset-0 z-[10020] flex items-center justify-center bg-black/80 backdrop-blur-sm p-2 sm:p-4 cursor-pointer overflow-hidden"
                    onClick={close}
                    style={{ animation: 'fadeIn 0.2s ease-out' }}
                >
                    <button
                        type="button"
                        onClick={close}
                        aria-label="Fermer"
                        className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>

                    {/* Contenu en grand sur carte blanche (lisible) : la plus grande taille qui tient dans l'écran, jamais d'ascenseur */}
                    <div
                        className="bg-white rounded-2xl p-2 sm:p-3 overflow-hidden shadow-2xl flex items-center justify-center"
                        onClick={(e) => e.stopPropagation()}
                        style={isImage ? styleAjuste(ratio) : { maxWidth: '92vw', maxHeight: '86vh' }}
                    >
                        {isImage ? (
                            <img
                                src={imageSrc}
                                alt="Réponse agrandie"
                                className={ratio ? 'w-full h-full object-contain' : 'max-w-[86vw] max-h-[78vh] object-contain'}
                                onLoad={(e) => { const im = e.currentTarget; if (im.naturalWidth && im.naturalHeight) setRatioNaturel(im.naturalWidth / im.naturalHeight); }}
                            />
                        ) : (
                            /* SVG / équation : les composants compacts ont une taille fixe
                               (text-sm, max-h-200px, w-full sans largeur). On force un grand rendu :
                               le svg (droite/geogebra) remplit la largeur, borné par la hauteur de l'écran
                               (viewBox : il se réduit en gardant ses proportions), le KaTeX (équation) grossit. */
                            <div className="pointer-events-none w-[82vw] max-w-[900px] flex items-center justify-center text-slate-900 [&_.katex]:!text-[1.8rem] sm:[&_.katex]:!text-[3rem] [&_svg]:!w-full [&_svg]:!h-auto [&_svg]:!max-h-[76vh]">
                                <ChoixRenderer choix={choix} light={light} />
                            </div>
                        )}
                    </div>

                    <p className="absolute bottom-2 sm:bottom-4 text-white/50 text-xs">Cliquer en dehors pour fermer</p>
                </div>,
                document.body
            )}
        </>
    );
});

export default ChoixZoomButton;
